import { useEffect, useRef, useState } from "react";
import { submitDeadlineSnapshot } from "../../services/camunda.service";
import { blockingTimestamp } from "../../services/assignmentDeadline";

export default function useAssignmentDeadline({ taskId, questionnaireId, dueAt, hardDeadline, deadlineAt, timerExpiresAt, clockOffset = 0, readOnly, answersRef, draft, submitting }) {
  const [now, setNow] = useState(Date.now());
  const [status, setStatus] = useState("idle");
  const snapshot = useRef(null);
  const flight = useRef(false);
  const latest = useRef({});
  latest.current = { draft, submitting };
  const cutoff = blockingTimestamp({ dueAt, hardDeadline, deadlineAt, timerExpiresAt });
  const closed = !readOnly && Number.isFinite(cutoff) && now + clockOffset >= cutoff;

  useEffect(() => {
    if ((!dueAt && !Number.isFinite(cutoff)) || readOnly) return;
    const tick = () => setNow(Date.now());
    const timer = setInterval(tick, 250);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", tick); window.removeEventListener("focus", tick); };
  }, [dueAt, cutoff, readOnly]);

  const saved = status === "saved";
  useEffect(() => {
    if (!closed || saved) return;
    if (!snapshot.current && draft.ready) {
      // Capture once. Retries send this frozen snapshot, never edits made after the deadline.
      snapshot.current = { questionnaireId, answers: draft.recovery || answersRef.current };
      draft.backup(snapshot.current.answers);
      if (draft.recovery) draft.recover?.();
    }
    const finish = async () => {
      if (Date.now() + clockOffset >= cutoff + 30000) { setStatus("failed"); return; }
      if (!snapshot.current || flight.current || latest.current.submitting) return;
      flight.current = true;
      setStatus("saving");
      try {
        await latest.current.draft.pause();
        if (Date.now() + clockOffset >= cutoff + 30000) { setStatus("failed"); return; }
        await submitDeadlineSnapshot(taskId, snapshot.current);
        latest.current.draft.complete();
        setStatus("saved");
      } catch {
        setStatus("failed");
      } finally { flight.current = false; }
    };
    finish();
    const timer = setInterval(finish, 3000);
    window.addEventListener("online", finish);
    return () => { clearInterval(timer); window.removeEventListener("online", finish); };
  }, [closed, saved, draft.ready, questionnaireId, taskId, cutoff, clockOffset, answersRef]); // eslint-disable-line react-hooks/exhaustive-deps

  return { closed, status, snapshot: snapshot.current, remainingSeconds: Math.max(0, Math.ceil((cutoff - now - clockOffset) / 1000)),
    expired: Number.isFinite(Date.parse(dueAt)) && now + clockOffset >= Date.parse(dueAt) };
}
